/**
 * GxSlider: a stepped choice along an ordered scale (a lever's slider control). A native range input for keyboard and screen
 * readers, drawn on the palette: navy fill and thumb, one tick per stop, the first and last stop named under the track (each one a click target for its stop),
 * or every stop with `stopLabels`. A click anywhere on the track snaps to the nearest stop.
 */
import type { CSSProperties } from "react";

export function GxSlider({ label, stops, index, disabled = false, allowed, stopLabels = false, onPick, onRefuse }: {
  readonly label: string;
  /** Stop labels in order. */
  readonly stops: readonly string[];
  readonly index: number;
  readonly disabled?: boolean;
  /** The stops a cap or floor leaves open (indices, inclusive); the others are drawn as a limit and a pick past one stops at the edge. */
  readonly allowed?: { readonly min: number; readonly max: number } | undefined;
  /** Name every stop under its tick, not only the first and last (a short practice scale). */
  readonly stopLabels?: boolean;
  readonly onPick: (index: number) => void;
  /** A pick on a disabled slider (a held decision) is answered here, never applied. */
  readonly onRefuse?: (() => void) | undefined;
}) {
  const last = Math.max(1, stops.length - 1);
  const fill = { "--gx-slider-fill": `${(index / last) * 100}%` } as CSSProperties;
  return (
    <div className="gx-slider" data-disabled={disabled || undefined}>
      <div className="gx-slider-track" style={fill} onClick={(e) => {
        // The nearest stop to the click, from the track's own width less the thumb's inset on each side.
        const box = e.currentTarget.getBoundingClientRect();
        const inset = Number.parseFloat(getComputedStyle(e.currentTarget).getPropertyValue("--gx-slider-inset")) || 0;
        const span = box.width - 2 * inset;
        if (disabled) onRefuse?.(); else if (span > 0) onPick(Math.min(last, Math.max(0, Math.round(((e.clientX - box.left - inset) / span) * last))));
      }}>
        <input type="range" min={0} max={stops.length - 1} step={1} value={index} aria-label={label} aria-valuetext={stops[index]}
          aria-disabled={disabled || undefined} onChange={(e) => { if (disabled) onRefuse?.(); else onPick(Number(e.target.value)); }} />
        <div className="gx-slider-ticks" aria-hidden="true">{stops.map((s, i) => <span key={s} data-on={i <= index || undefined} data-blocked={allowed !== undefined && (i < allowed.min || i > allowed.max) || undefined} />)}</div>
      </div>
      {stopLabels ? (
        <div className="gx-slider-stops" aria-hidden="true">
          {stops.map((name, at) => <button key={name} type="button" tabIndex={-1} aria-disabled={disabled || undefined} style={{ "--gx-stop": at / last } as CSSProperties} onClick={() => { if (disabled) onRefuse?.(); else onPick(at); }}>{name}</button>)}
        </div>
      ) : (
        <div className="gx-slider-ends" aria-hidden="true">
          {[[0, stops[0]], [stops.length - 1, stops.at(-1)]].map(([at, name]) => (
            <button key={at} type="button" tabIndex={-1} aria-disabled={disabled || undefined} onClick={() => { if (disabled) onRefuse?.(); else onPick(at as number); }}>{name}</button>
          ))}
        </div>
      )}
    </div>
  );
}
